const DEFAULT_MIN_WIDTH_PX = 100;

const RIGHT_ALIGNED_VALUES_TYPES = new Set([
  "number",
  "number_not_abbreviated",
  "whole_number",
  "trend_status"
]);

const RIGHT_ALIGNED_ATTRIBUTE_NAMES = new Set([
  "zone_parameters",
  "active_risk_reward"
]);

/** @type {Record<string, number>} */
export const UNIVERSAL_TABLE_LITE_COLUMN_MIN_WIDTH_PX_BY_ATTRIBUTE = {
  ticker_as_link: 88,
  ticker: 88,
  Price: 96,
  price: 104,
  daily_change: 104,
  market: 80,
  trend_status: 70,
  sector: 200,
  company_name: 150,
  description: 280,
  created_at: 150,
  confirmation_date: 150,
  sent_at: 150,
  "action.price_alert": 70,
  capitalization: 120,
  stock_situation_classification: 140,
  daily_change_date_from_backend: 150,
  average_daily_volume_in_stock_50_quotes: 140,
  stock_trend: 130,
  stock_beta_parameter: 96,
  average_daily_change_in_stock_50_quotes_from_backend: 140,
  average_volume_change_from_firebase: 128,
  priceearnings_ratio: 88,
  price_to_book_value_ratio: 88,
  price_to_ebit_ratio: 96,
  price_to_sale_ratio: 96,
  net_gross_profit: 112,
  gross_profit_margin: 112,
  operating_profit_margin: 112,
  current_financial_liquidity: 112,
  debt_ratio: 96,
  roe_ratio: 88,
  roa_ratio: 88,
  next_dividend_payout_value: 120,
  next_dividends_yield: 112,
  next_dividend_day: 150,
  last_day_with_right_to_last_dividend_day: 150,
  rate_of_returns_1w: 104,
  rate_of_returns_1m: 104,
  rate_of_returns_6m: 104,
  rate_of_returns_1y: 104,
  rate_of_returns_ytd: 104,
  rate_of_returns_3y: 104,
  rate_of_returns_5y: 104,
  zone_parameters: 120,
  active_risk_reward: 120,
  stop_loss: 108,
  take_profit: 96
};

/** Tighter bases for the supply/demand signals columns that were leaving the widest gaps. */
const FIT_COLUMN_MIN_WIDTH_PX_BY_ATTRIBUTE = {
  ticker_as_link: 72,
  type: 72,
  detail_type: 84,
  position_rate_of_return: 76,
  entry_level: 96,
  current_price: 92,
  stop_loss: 108,
  take_profit: 96,
  risk_reward: 80,
  state: 88,
  alert_datetime: 104,
  zone_parameters: 112
};

const FIT_COLUMN_LOCKED_ATTRIBUTES = new Set(["zone_parameters"]);
const FIT_SINGLE_LINE_ZONE_PARAMETERS_MIN_WIDTH_PX = 144;

const FIT_COLUMN_FLOOR_PX = 72;

/**
 * @param {object | undefined | null} column — tab column descriptor from config API
 * @returns {number}
 */
export function getUniversalTableLiteColumnMinWidthPx(column) {
  if (!column || typeof column.attribute_name !== "string") {
    return DEFAULT_MIN_WIDTH_PX;
  }

  const fromApi = column.min_width_px ?? column.minWidthPx ?? column.min_width;
  if (typeof fromApi === "number" && fromApi > 0) {
    return fromApi;
  }

  return (
    UNIVERSAL_TABLE_LITE_COLUMN_MIN_WIDTH_PX_BY_ATTRIBUTE[
      column.attribute_name
    ] ?? DEFAULT_MIN_WIDTH_PX
  );
}

/**
 * Inline styles for `<th>` / `<td>` flex cells (UniversalTableLiteWeb layout).
 * @param {object | undefined | null} column
 * @returns {{ flexGrow: number, flexShrink: number, flexBasis: number, minWidth: number }}
 */
function getFitColumnMinWidthPx(column) {
  const attributeName = column?.attribute_name;

  if (
    typeof attributeName === "string" &&
    FIT_COLUMN_MIN_WIDTH_PX_BY_ATTRIBUTE[attributeName]
  ) {
    return FIT_COLUMN_MIN_WIDTH_PX_BY_ATTRIBUTE[attributeName];
  }

  return getUniversalTableLiteColumnMinWidthPx(column);
}

export function getUniversalTableLiteColumnCellStyle(column, options = {}) {
  if (options.fitColumns) {
    const isZoneParameters = FIT_COLUMN_LOCKED_ATTRIBUTES.has(
      column?.attribute_name
    );
    const px =
      isZoneParameters && options.singleLineZoneParameters
        ? FIT_SINGLE_LINE_ZONE_PARAMETERS_MIN_WIDTH_PX
        : getFitColumnMinWidthPx(column);

    return {
      flexGrow: 1,
      flexShrink: isZoneParameters ? 0 : 1,
      flexBasis: px,
      minWidth: isZoneParameters ? px : Math.min(px, FIT_COLUMN_FLOOR_PX)
    };
  }

  const px = getUniversalTableLiteColumnMinWidthPx(column);
  return {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: px,
    minWidth: px
  };
}

/**
 * @param {object | undefined | null} column
 * @returns {boolean}
 */
export function getUniversalTableLiteColumnAlignRight(column) {
  if (!column) {
    return false;
  }

  if (
    typeof column.attribute_name === "string" &&
    RIGHT_ALIGNED_ATTRIBUTE_NAMES.has(column.attribute_name)
  ) {
    return true;
  }

  if (typeof column.values_type !== "string") {
    return false;
  }

  return RIGHT_ALIGNED_VALUES_TYPES.has(column.values_type);
}

/**
 * @param {object | undefined | null} column
 * @returns {string | undefined}
 */
export function getUniversalTableLiteColumnCellClassName(column) {
  return getUniversalTableLiteColumnAlignRight(column)
    ? "universal-table-lite-cell--align-right"
    : undefined;
}

/**
 * SquaberTable cell props (flex sizing + optional right alignment by values_type).
 * @param {object | undefined | null} column
 * @returns {{ style: object, className?: string }}
 */
export function getUniversalTableLiteColumnCellProps(column, options = {}) {
  const className = getUniversalTableLiteColumnCellClassName(column);

  return {
    style: getUniversalTableLiteColumnCellStyle(column, options),
    ...(className ? { className } : {})
  };
}
